import { useState } from "react";
import { IconCalendar, IconX } from "@tabler/icons-react";
import { formatBnDate } from "@/utils/bnFormat";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

/**
 * Shared date picker — Popover + shadcn Calendar, matching the profile page picker.
 * Replaces native <input type="date"> so the picker looks consistent everywhere.
 *
 * Props:
 *   value        - selected Date object, or null
 *   onChange     - called with the new Date object (or null when cleared)
 *   placeholder  - text shown when no date is selected
 *   triggerClassName - classes for the trigger button (defaults to "filter-field")
 *   fromYear / toYear - calendar year range (defaults: 2000 → current year)
 *   disabled     - react-day-picker matcher for days to disable (e.g. { after: new Date() })
 *   clearable    - when true, show an X to clear the selection (calls onChange(null))
 *   formatValue  - render the chosen date yourself, for a form that writes dates
 *                  its own way (Bengali numerals, দিন-মাস-বছর, …)
 */
export default function DatePickerField({
  id,
  value,
  onChange,
  placeholder = "dd/mm/yyyy",
  triggerClassName = "filter-field",
  fromYear = 2000,
  toYear = new Date().getFullYear(),
  contentClassName = "",
  disabled,
  clearable = false,
  formatValue,
}) {
  const [open, setOpen] = useState(false);
  const showClear = clearable && !!value;
  return (
    <div className="relative w-full">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button id={id} type="button" className={`${triggerClassName} w-full flex items-center gap-2.5 text-left ${showClear ? 'pr-9' : ''}`}>
            <IconCalendar size={16} className="text-zinc-400 flex-shrink-0" />
            <span className={value ? "text-zinc-800 dark:text-zinc-100" : "text-zinc-400 dark:text-zinc-500"} style={{ fontSize: 'inherit' }}>
              {value ? (formatValue ? formatValue(value) : formatBnDate(value, { withYear: true })) : placeholder}
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent className={`w-auto p-0 ${contentClassName}`} align="start">
          <Calendar
            mode="single"
            captionLayout="dropdown"
            selected={value || undefined}
            // A day is the whole answer here, so picking one closes the popover
            // the way the native date input did.
            onSelect={(date) => { onChange(date || null); if (date) setOpen(false); }}
            fromYear={fromYear}
            toYear={toYear}
            defaultMonth={value || undefined}
            disabled={disabled}
          />
        </PopoverContent>
      </Popover>
      {showClear && (
        <button
          type="button"
          aria-label="Clear date"
          onClick={(e) => { e.stopPropagation(); onChange(null); }}
          className="absolute top-1/2 right-2.5 -translate-y-1/2 p-0.5 rounded-full text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
        >
          <IconX size={15} />
        </button>
      )}
    </div>
  );
}
